import { baseApi } from "../baseApi";
import {
  CreateFolderRequest,
  CreateFolderResponse,
  DeleteFileResponse,
  DeleteFolderResponse,
  EditFileRequest,
  EditFileResponse,
  EditFolderRequest,
  EditFolderResponse,
  EditShareRequest,
  FileDetailResponse,
  GetFileSharesResponse,
  GetFolderItemsResponse,
  GetRecentVaultFilesResponse,
  ShareFileRequest,
  ShareFileResponse,
  UploadFileResponse,
} from "./types";

export const vaultApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Upload
    upload: build.mutation<UploadFileResponse, FormData>({
      query: (formData) => ({
        url: "file-vault/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["FolderItems"],
    }),

    // Folder Items
    getFolderItems: build.query<
      GetFolderItemsResponse,
      {
        folderId?: string | null;
        page?: number;
        search?: string;
        limit?: number;
      }
    >({
      query: ({ folderId, page, search, limit }) => ({
        url: `file-vault/folder-items`,
        params: {
          folderId,
          page,
          search,
          limit,
        },
      }),
      providesTags: ["FolderItems"],
    }),

    // Show File (URL)
    showFile: build.query<string, string>({
      query: (id: string) => ({
        url: `file-vault/file/${id}`,
        method: "GET",
        transformResponse: (response: string) => response, // since it's plain text
        responseHandler: (response: Response) => response.text(), // 🔑 parse as text
      }),
    }),
    downloadFile: build.query<Blob, string>({
      query: (id: string) =>
        ({
          url: `file-vault/download/${id}`,
          method: "GET",
          responseHandler: async (response: Response) => response.blob(),
        } as any),
    }),
    fileDetails: build.query<FileDetailResponse, string>({
      query: (id: string) => ({
        url: `file-vault/file/${id}/details`,
      }),
    }),
    // Edit File
    editFile: build.mutation<EditFileResponse, EditFileRequest>({
      query: (data) => ({
        url: `file-vault/file/${data.id}`,
        method: "PATCH",
        body: data.body,
      }),
      invalidatesTags: ["FolderItems"],
    }),

    // Delete File
    deleteFile: build.mutation<DeleteFileResponse, string>({
      query: (id) => ({
        url: `file-vault/file/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["FolderItems", "DirectChatMessages"],
    }),

    // Share File
    shareFile: build.mutation<ShareFileResponse, ShareFileRequest>({
      query: (data) => ({
        url: `file-vault/file/${data.id}/share`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["Shares"],
    }),

    // Get File Shares
    getFileShares: build.query<GetFileSharesResponse, string>({
      query: (id) => ({
        url: `file-vault/file/${id}/shares`,
      }),
      providesTags: ["Shares"],
    }),

    // Edit Shares
    editShares: build.mutation<
      { message: string; statusCode: number },
      EditShareRequest
    >({
      query: (data) => ({
        url: `file-vault/share/${data.id}/edit`,
        method: "PUT",
        body: data.body,
      }),
      invalidatesTags: ["Shares"],
    }),

    // Create Folder
    createFolder: build.mutation<CreateFolderResponse, CreateFolderRequest>({
      query: (data) => ({
        url: `file-vault/create-folder`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["FolderItems"],
    }),

    // Edit Folder
    editFolder: build.mutation<EditFolderResponse, EditFolderRequest>({
      query: (data) => ({
        url: `file-vault/folder/${data.id}`,
        method: "PATCH",
        body: data.body,
      }),
      invalidatesTags: ["FolderItems"],
    }),

    // Delete Folder
    deleteFolder: build.mutation<DeleteFolderResponse, string>({
      query: (id) => ({
        url: `file-vault/folder/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["FolderItems"],
    }),

    getRecentVaultFiles: build.query<GetRecentVaultFilesResponse, void>({
      query: () => ({
        url: "file-vault/recent-files",
        method: "GET",
      }),
      providesTags: ["FolderItems"],
    }),
  }),
});

export const {
  useUploadMutation,
  useGetFolderItemsQuery,
  useLazyShowFileQuery,
  useShowFileQuery,
  useLazyDownloadFileQuery,
  useDownloadFileQuery,
  useEditFileMutation,
  useDeleteFileMutation,
  useShareFileMutation,
  useGetFileSharesQuery,
  useEditSharesMutation,
  useCreateFolderMutation,
  useEditFolderMutation,
  useDeleteFolderMutation,
  useFileDetailsQuery,
  useGetRecentVaultFilesQuery,
} = vaultApi;
